import { useMemo } from "react";
import type {
  Block,
  TableBlockData,
  TableRow,
  TextPart,
} from "../../../../models/ManualAuthoring";
import {
  buildHeaderRows,
  getLeafColumnWidthMap,
  getLeafColumns,
} from "../../tableBuilderUtils/previewUtils";
import "./tableBlock.css";

function serializeCellDisplay(parts: TextPart[]): string {
  return parts
    .map((part) => (part.type === "text" ? part.value : `[${part.label}]`))
    .join("");
}

function getCellValue(row: TableRow, key: string): string {
  const parts = row.cells.find((cell) => cell.key === key)?.value || [];
  return serializeCellDisplay(parts);
}

export default function TableView({
  block,
  tableNumber,
}: {
  block: Block;
  tableNumber?: number;
}) {
  const data = block.data as TableBlockData;
  const rows = data.rows || [];
  const tableTitle = data.displayTitle || data.templateName || "Untitled Table";
  const titleText = tableNumber ? `Table ${tableNumber}. ${tableTitle}` : tableTitle;

  const headerRows = useMemo(
    () => buildHeaderRows(data.columns ?? []),
    [data.columns]
  );
  const leafColumns = useMemo(
    () => getLeafColumns(data.columns ?? []),
    [data.columns]
  );
  const leafWidthMap = useMemo(
    () => getLeafColumnWidthMap(data.columns ?? []),
    [data.columns]
  );

  if (!leafColumns.length) {
    return <div className="pdf-block pdf-table-block__empty">No table columns configured.</div>;
  }

  const justifyContent =
    data.alignment === "center"
      ? "center"
      : data.alignment === "right"
        ? "flex-end"
        : "flex-start";

  return (
    <div className="pdf-block pdf-table-block pdf-table-block--view">
      <div className="pdf-table-block__title">{titleText}</div>

      <div className="pdf-table-block__shell" style={{ justifyContent }}>
        <table className="pdf-table-block__table" style={{ width: data.width || "100%" }}>
          <colgroup>
            {leafColumns.map((column) => (
              <col
                key={`view-col-${column.id}`}
                style={
                  leafWidthMap[column.id]
                    ? { width: `${leafWidthMap[column.id]}%` }
                    : undefined
                }
              />
            ))}
          </colgroup>
          <thead>
            {headerRows.map((row, rowIndex) => (
              <tr key={`head-${rowIndex}`}>
                {row.map((cell) => (
                  <th
                    key={cell.id}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan}
                    style={cell.width ? { width: `${cell.width}%` } : undefined}
                  >
                    {cell.header}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {(rows.length ? rows : [{ cells: [] }]).map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {leafColumns.map((column) => {
                  const key = column.key || column.id;
                  const value = rows.length ? getCellValue(row as TableRow, key) : "";
                  return (
                    <td
                      key={`${rowIndex}-${key}`}
                      style={{
                        ...(column.align
                          ? {
                              textAlign: column.align as "left" | "center" | "right",
                            }
                          : {}),
                        ...(column.wrap
                          ? {
                              whiteSpace: "normal",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }
                          : {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }),
                      }}
                      title={value}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
